// Backlog 073-D — `cloudgrid scaffold env-example`. Greps each declared
// service's source for `process.env.X` references and merges any new keys
// into `services/<svc>/.env.example`. Idempotent: existing content is never
// rewritten; only missing keys get appended (each as a `KEY=` placeholder).
//
// Match forms supported:
//   process.env.FOO
//   process.env["FOO"]
//   process.env['FOO']
//   process.env[`FOO`]
//
// Skips node_modules, .git, and a small set of common build dirs anywhere
// in the tree.

import { Command } from 'commander';
import {
  existsSync,
  readdirSync,
  readFileSync,
  statSync,
  writeFileSync,
} from 'node:fs';
import { join } from 'node:path';
import { parse as parseYaml } from 'yaml';
import { CloudGridError } from '../../utils.js';
import { handleV2Error } from '../../handle-error.js';
import { parseDotenvKeys } from '../../services/env-preflight.js';

interface CloudGridYamlShape {
  services?: Record<string, unknown>;
}

const SCANNED_EXTENSIONS = new Set([
  '.ts',
  '.tsx',
  '.js',
  '.jsx',
  '.mjs',
  '.cjs',
]);
const SKIP_DIRS = new Set([
  'node_modules',
  '.git',
  'dist',
  '.next',
  '__pycache__',
  '.venv',
]);

// Mirrors the server-side `KEY_RE` in
// `packages/api/src/routes/v2/runtimes-ops.ts` (`[-._a-zA-Z][-._a-zA-Z0-9]*`).
// Keeping the client's scanner regex aligned with the server's parser means
// the generated `.env.example` covers every key the server would accept;
// anything narrower would silently omit keys (e.g. `process.env['-my-key']`).
// The dotted form (`process.env.X`) still compiles in JS only for plain
// identifiers, so in practice the leading dot/dash classes only match in the
// bracket form — but we widen both halves to keep the regex symmetrical with
// the server.
const PROCESS_ENV_RE =
  /process\.env\.([-._A-Za-z][-._A-Za-z0-9]*)|process\.env\[\s*['"`]([-._A-Za-z][-._A-Za-z0-9]*)['"`]\s*\]/g;

/**
 * Walk a service folder and return the set of `process.env.<KEY>` keys
 * referenced by any source file. Returns alphabetically-sorted, deduplicated
 * keys. If the folder doesn't exist, returns [].
 */
export function scanProcessEnvKeys(serviceDir: string): string[] {
  if (!existsSync(serviceDir)) return [];
  const found = new Set<string>();
  walk(serviceDir, found);
  return [...found].sort();
}

function walk(dir: string, into: Set<string>): void {
  let entries: ReadonlyArray<string>;
  try {
    entries = readdirSync(dir);
  } catch {
    return;
  }
  for (const name of entries) {
    if (SKIP_DIRS.has(name)) continue;
    const path = join(dir, name);
    let st;
    try {
      st = statSync(path);
    } catch {
      continue;
    }
    if (st.isDirectory()) {
      walk(path, into);
      continue;
    }
    if (!st.isFile()) continue;
    const dotIdx = name.lastIndexOf('.');
    if (dotIdx < 0) continue;
    const ext = name.slice(dotIdx);
    if (!SCANNED_EXTENSIONS.has(ext)) continue;
    let content: string;
    try {
      content = readFileSync(path, 'utf-8');
    } catch {
      continue;
    }
    let m: RegExpExecArray | null;
    PROCESS_ENV_RE.lastIndex = 0;
    while ((m = PROCESS_ENV_RE.exec(content)) !== null) {
      const key = m[1] ?? m[2];
      if (key) into.add(key);
    }
  }
}

export interface RunScaffoldEnvExampleOpts {
  cwd?: string;
}

export async function runScaffoldEnvExample(
  opts: RunScaffoldEnvExampleOpts = {},
): Promise<void> {
  const cwd = opts.cwd ?? process.cwd();
  const yamlPath = join(cwd, 'cloudgrid.yaml');
  if (!existsSync(yamlPath)) {
    throw new CloudGridError(
      'No cloudgrid.yaml found. Run: cloudgrid init <kind> <name>',
    );
  }
  let parsed: CloudGridYamlShape;
  try {
    parsed = parseYaml(readFileSync(yamlPath, 'utf-8')) as CloudGridYamlShape;
  } catch (err) {
    throw new CloudGridError(
      `Could not parse cloudgrid.yaml: ${(err as Error).message}`,
    );
  }
  const services = parsed?.services ?? {};
  const names = Object.keys(services);
  if (names.length === 0) {
    process.stdout.write('No services declared in cloudgrid.yaml.\n');
    return;
  }

  for (const name of names) {
    const serviceDir = join(cwd, 'services', name);
    const found = scanProcessEnvKeys(serviceDir);
    if (found.length === 0) continue;
    const examplePath = join(serviceDir, '.env.example');
    const existing = existsSync(examplePath)
      ? readFileSync(examplePath, 'utf-8')
      : '';
    const declared = new Set(parseDotenvKeys(existing));
    const toAdd = found.filter((k) => !declared.has(k));
    if (toAdd.length === 0) {
      process.stdout.write(
        `  ${name}: up to date (${found.length} keys).\n`,
      );
      continue;
    }
    const prefix =
      existing.length === 0 ? '' : existing.endsWith('\n') ? '' : '\n';
    const appended = prefix + toAdd.map((k) => `${k}=`).join('\n') + '\n';
    writeFileSync(examplePath, existing + appended);
    process.stdout.write(
      `  ${name}: +${toAdd.length} key${toAdd.length === 1 ? '' : 's'} (${toAdd.join(', ')})\n`,
    );
  }
}

export const scaffoldEnvExampleCommand = new Command('env-example')
  .description(
    'Generate or update services/<svc>/.env.example from process.env.X references in source',
  )
  .action(async () => {
    try {
      await runScaffoldEnvExample();
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });
